import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Save, Settings2, AtSign } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi } from '../../api/client';

const CATEGORY_ICONS = {
  general: 'Informations du site',
  contact: 'Contact',
  social: 'Réseaux sociaux',
};

const CATEGORY_ORDER = ['general', 'contact', 'social'];

export default function AdminSettingsPage() {
  const { t } = useLanguage();
  const a = t.dashboard?.admin || {};

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.settings();
      const list = Array.isArray(res.settings) ? res.settings : [];
      setSettings(list.sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        if (ai !== bi) return ai - bi;
        return (a.label || a.key).localeCompare(b.label || b.key);
      }));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showToast(err.data?.error || 'Erreur de chargement des paramètres', 'error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }

  function handleChange(index, value) {
    setSettings((prev) =>
      prev.map((s, i) => (i === index ? { ...s, value } : s))
    );
  }

  async function handleSave() {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings.filter((s) => s.key !== 'siteName').map((s) => ({
        key: s.key,
        value: s.value,
        label: s.label,
        category: s.category,
      })));
      showToast('Paramètres enregistrés avec succès', 'success');
    } catch (err) {
      showToast(err.data?.error || "Échec de l'enregistrement", 'error');
    } finally {
      setSaving(false);
    }
  }

  const byCategory = settings.reduce((acc, s) => {
    const cat = s.category || 'Général';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const categoryTitle = (cat) => CATEGORY_ICONS[cat] || cat;

  function renderSection(cat, items) {
    const isSocial = cat === 'social';
    return (
      <section className="dform-section" key={cat}>
        <header className="dform-section-head">
          {isSocial ? <AtSign size={17} /> : <Settings2 size={17} />}
          <h4>
            {categoryTitle(cat)}
            {isSocial && items.length > 0 ? ` (${items.length})` : ''}
          </h4>
        </header>
        <div
          className="dform-grid"
          style={isSocial ? { gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' } : undefined}
        >
          {items.filter((s) => s.key !== 'siteName').map((s) => {
            const globalIndex = settings.findIndex((x) => x.key === s.key);
            return (
              <div className="dform-field" key={s.key}>
                <label htmlFor={s.key}>{s.label || s.key}</label>
                {s.key === 'siteDescription' ? (
                  <textarea
                    id={s.key}
                    className="dform-input"
                    rows={4}
                    style={{ minHeight: 100, resize: 'vertical' }}
                    value={s.value || ''}
                    onChange={(e) => handleChange(globalIndex, e.target.value)}
                    onBlur={handleSave}
                    disabled={saving}
                  />
                ) : (
                  <input
                    id={s.key}
                    type="text"
                    className="dform-input"
                    value={s.value || ''}
                    onChange={(e) => handleChange(globalIndex, e.target.value)}
                    onBlur={handleSave}
                    disabled={saving}
                    placeholder={s.key.includes('social') ? 'https://…' : ''}
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <>
        <header className="dash-page-head">
          <h1>{a.settingsTitle || 'Paramètres'}</h1>
          <p>{a.settingsSubtitle || 'Configuration de la plateforme.'}</p>
        </header>
        <p>Chargement…</p>
      </>
    );
  }

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.settingsTitle || 'Paramètres'}</h1>
        <p>{a.settingsSubtitle || 'Configuration de la plateforme.'}</p>
      </header>

      {toast && (
        <div className={`admin-toast ${toast.type === 'error' ? 'admin-toast--error' : ''}`} role="status">
          {toast.type === 'error' ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      <div className="dform">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {CATEGORY_ORDER.slice(0, 2).map((cat) =>
            byCategory[cat] ? renderSection(cat, byCategory[cat]) : null
          )}
        </div>

        {byCategory.social && renderSection('social', byCategory.social)}

        <footer className="dform-footer">
          <span className="dform-spacer" />
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </div>
    </>
  );
}
